import { isEqual, keys, omit } from 'lodash';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FB, FBMediaProps, FBMPIProcedureProps, FBMPIProcedureState, FBProcedureItemType } from '..';
import { companySelectors } from '../../../state/ducks/company';

export const withFBMPIProcedure = <T extends FBMPIProcedureProps>(
  Component: React.FunctionComponent<T>,
) => {
  const Comp = ({
    handleAdd,
    onKeyDown,
    mpiProcedureState,
    name = '',
    isInputOwner,
    isOutput,
    isPreview,
    disabled,
    types,
    index = 0,
    shouldSkipSerialization,
    ...props
  }: T) => {
    const isFieldDisabled = disabled;
    // MARK: @config
    const { workspaceState, formState } = FB.useStores();
    const schemaItem = workspaceState?.getSchemaItemAt(index);
    const editorCfg = schemaItem?.editorConfig;
    const deviation = editorCfg?.deviation;
    const hasDeviation = Boolean(deviation);
    const baseEditorConfig = editorCfg ? { ...editorCfg, deviation: undefined } : undefined;

    // Shared deviation-toggle state via formInputSync so all steps read the same value
    const DEVIATION_HIDE_KEY = 'deviation-hide-changes';
    const hideDeviationChanges = useObserver(() =>
      Boolean(workspaceState?.formInputSync.get(DEVIATION_HIDE_KEY)),
    );
    const onToggleHideDeviation = () => {
      workspaceState?.formInputSync.set(DEVIATION_HIDE_KEY, !workspaceState.formInputSync.get(DEVIATION_HIDE_KEY));
    };

    // Only the first mpiprocedure step that has a deviation renders the toggle
    const allMpiSteps = workspaceState?.schema?.filter((s) => !s.deleted && s.type === 'mpiprocedure');
    const firstDeviatedStep = allMpiSteps?.find((s) => Boolean(s.editorConfig?.deviation));
    const isFirstDeviatedStep = Boolean(firstDeviatedStep && firstDeviatedStep === workspaceState?.schema?.[index]);
    const resolvedConfig = (hasDeviation && deviation)
      ? {
        ...editorCfg,
        materials: deviation.materials ?? editorCfg?.materials,
        equipment: deviation.equipment ?? editorCfg?.equipment,
        description: deviation.description ?? editorCfg?.description,
        attachments: deviation.attachments ?? editorCfg?.attachments,
      }
      : editorCfg;
    mpiProcedureState = FB.useRef(FBMPIProcedureState, resolvedConfig);
    const redlineActive = useSelector(companySelectors.getRedlineActive);

    isOutput = Boolean(workspaceState?.isOutput && workspaceState?.id);
    isPreview = (isOutput || workspaceState?.mode === 'preview' || workspaceState?.mode === 'formPreview')
    && !redlineActive;
    types = useObserver(() => keys(FBProcedureItemType).filter((type) => {
      const attachments = mpiProcedureState?.value?.attachments;
      switch (type) {
        case FBProcedureItemType.serials:
          return !shouldSkipSerialization;
        case FBProcedureItemType.attachments:
          return !isPreview || (attachments?.length ?? 0) > 0;
        default: {
          const itemValues = mpiProcedureState?.[type] ?? [];
          return !isPreview || itemValues.length > 0;
        }
      }
    }));
    isInputOwner = workspaceState?.getIsInputOwner(name);
    disabled = (!isInputOwner || isPreview) && !redlineActive;
    const descFormValue = formState?.getFieldValue(`${name}.description`);
    if (!descFormValue) {
      formState?.setFieldValue(`${name}.description`, props.editorConfig?.description);
    }
    // MARK: @reactions
    React.useEffect(() => reaction(
      () => mpiProcedureState?.value,
      (data) => {
        const schemaItem = workspaceState?.getSchemaItemAt(index);
        if (workspaceState && schemaItem) {
          workspaceState.setSchemaItemAt({
            ...schemaItem,
            editorConfig: {
              ...schemaItem?.editorConfig,
              ...data,
            },
          }, index);
          workspaceState.saveDocRev(formState?.getValues());
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), []);

    useEffect(() => {
      reaction(
        () => formState?.inputState.get(`${name}.attachments`)?.value,
        (attachments) => {
          if (!mpiProcedureState) { return; }
          const { attachments: stateAttachments } = mpiProcedureState.value || {};
          if (isEqual((attachments as FBMediaProps[]).sort(), (stateAttachments || []).sort())) { return; }
          mpiProcedureState.value = {
            ...mpiProcedureState.value,
            attachments,
          };
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => reaction(
      () => formState?.inputState.get(`${name}.description`)?.value,
      (description) => {
        if (!mpiProcedureState) { return; }
        mpiProcedureState.value = {
          ...mpiProcedureState.value,
          description,
        };
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), []);

    // MARK: @helpers

    // MARK: @methods
    handleAdd = (isClone?: boolean, isFromKeys?: boolean) => {
      if (!workspaceState) { return; }
      const newName = FB.uniqid;
      let schemaItem = workspaceState.getSchemaItemAt(index);
      if (!isClone) {
        schemaItem = omit(schemaItem, 'editorConfig');
      }
      const newItem = {
        ...schemaItem,
        autoScrollTo: isFromKeys,
        autoFocus: isFromKeys,
        name: newName,
      };
      workspaceState?.setSchemaItemAt(newItem, index + 1);
      workspaceState.saveDocRev(formState?.getValues());
    };

    onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && e.ctrlKey) {
        handleAdd?.(false, true);
      }
    };

    // MARK: @observer

    return Component({
      ...(props as T),
      handleAdd,
      onKeyDown,
      mpiProcedureState,
      isPreview,
      name,
      disabled,
      types,
      isFieldDisabled,
      hasDeviation,
      baseEditorConfig,
      hideDeviationChanges,
      isFirstDeviatedStep,
      onToggleHideDeviation,
      shouldSkipSerialization,
    });
  };

  Comp.displayName = 'withFBMPIProcedure';
  return Comp;
};
